// AuthCheckPermissionFlow (contract 06 §8) — pure decisions for the invited-user entry surface.
// The Vue composable owns toast + navigation. Query prose is never rendered: any non-empty
// `?message` is reduced to the fixed `auth.invite.expired` key before the URL is scrubbed.
import type { RouteLocationRaw } from 'vue-router'

import type { UserResponse } from '@/features/account'

/** Where an accepted invite lands when the inviting account is a web account (unchanged). */
const WEB_LANDING: RouteLocationRaw = { name: 'Summary' }
/** …and when it is MCP-only. The literal mirrors `MCP_HOME_ROUTE_NAME`; it cannot be imported,
 *  because a feature may not reach into `app/` (the one-way layer rule). */
const MCP_LANDING: RouteLocationRaw = { name: 'McpAdAccounts' }

export class AuthCheckPermissionFlow {
  showInviteExpiredAlert(message: unknown): boolean {
    if (typeof message === 'string') return message.trim().length > 0
    return Array.isArray(message) && message.some((item) => typeof item === 'string' && item.trim())
  }

  redirectIfUserHasPassword(user: UserResponse | null): boolean {
    // Google users arrive with an already authenticated identity and no local password. They must
    // skip the password form just like password users (New-Front checkPermissionAccess).
    return user?.has_password === true || user?.user?.is_google === true
  }

  /**
   * Where an accepted invite lands (ORP-21656 AC 7).
   *
   * The backend appends `&is_mcp_only=true|false` to the `/check-permission` redirect
   * (`accounts/views.py:847`) because only it knows the inviting account's product. This is the
   * ONE place that reads it, and it is a pure function so the screen keeps owning navigation.
   *
   * FAIL-SAFE WEB: anything that is not the exact string `'true'` — absent, empty, an array from a
   * repeated query key, a truthy-looking `'1'` — lands on the web app exactly as before. The
   * router's MCP branch is the backstop that moves a genuinely MCP-only user afterwards, so a
   * missed landing costs one extra redirect; a WRONG landing would strand a web invitee in a
   * product they do not own.
   */
  resolveInviteLanding(isMcpOnly: unknown): RouteLocationRaw {
    return isMcpOnly === 'true' ? MCP_LANDING : WEB_LANDING
  }
}
